/**
 * Roles and access.
 *
 * Two questions, two places:
 *   - Team > Permissions defines ROLES: what a role can do (capabilities).
 *   - A team member's profile ASSIGNS one role, and (multi-location only)
 *     the other locations that role applies at.
 *
 * Roles are separate from the consent layer (marketing, loyalty, client
 * sharing): a role is what someone can do for the salon; consent is what
 * the salon may do with a self-employed stylist's clients. A role never
 * overrides consent.
 *
 * The server is the source of truth and the enforcer (beu-node-v2
 * docs/permissions.md). Capability ids here MUST match permissionsCatalog.js.
 */
import { permissionsService } from '../../_services/permissions.service';
import { MESSAGES_VISIBLE } from '../../config/featureFlags';

/**
 * What a role can do, in the SAME plain words as Settings › Reception's
 * switches (deskSwitches.js) wherever the thing is the same, grouped the same
 * way. `catalogue` is never shown: the server derives it (on when any of
 * Services, Products or Gift cards is).
 */
export const CAPABILITY_GROUPS = [
  { id: 'bookings', name: 'Bookings', caps: [
    { id: 'diary_all', label: 'Diary', help: 'See and book the whole diary, every column, even when Diary sharing is off' },
    { id: 'pos', label: 'Take payments', help: "Check out anyone's booking. Everyone can check out their own" },
    { id: 'discounts', label: 'Discounts', help: "Take money off anyone's booking. Everyone can discount their own" },
    { id: 'discount_codes', label: 'Discount codes', help: "Make and change the salon's codes in Marketing. Anyone who takes payments can use one" },
    { id: 'refunds', label: 'Refunds', help: 'Give money back on salon sales, and change gift card balances' },
  ] },
  { id: 'clients', name: 'Clients', caps: [
    { id: 'clients_all', label: 'All clients', help: "The salon's shared clients, not just their own. Self-employed private clients stay private" },
    { id: 'client_notes', label: 'Client notes and forms', help: 'Notes, visit history, and consultation and medical forms, on clients they can see' },
    { id: 'clients_export', label: 'Export client lists', help: 'Self-employed can always export their own' },
  ].concat(MESSAGES_VISIBLE ? [
    // Texting plan, 14 Oct 2026: texts and emails to clients cost the salon
    { id: 'client_texts', label: 'Text and email clients', help: 'Send texts and emails from Messages and client pages. Each one costs the salon. Reading never needs this' },
  ] : []) },
  { id: 'menu', name: 'Your menu', caps: [
    { id: 'services', label: 'Services and prices', help: 'Services, prices, categories, forms, rooms and equipment, and taxes' },
    { id: 'products', label: 'Products and stock', help: 'Products, stock take, the online store and orders' },
    { id: 'gift_cards', label: 'Gift cards', help: 'Gift card designs and what you sell' },
  ] },
  { id: 'money', name: 'Money', caps: [
    { id: 'reports', label: 'Finance and reports', help: "Takings, transactions and reports, including other team members' takings" },
    { id: 'payouts', label: 'Payouts and bank details' },
  ] },
  { id: 'team', name: 'Team', caps: [
    { id: 'team', label: 'Staff and pay', help: 'Add or change team members, their pay and hours' },
  ] },
  { id: 'business', name: 'Business', caps: [
    { id: 'marketing', label: 'Marketing', help: 'Campaigns, automations and loyalty' },
    { id: 'settings', label: 'Settings', help: 'Business details, booking rules, card readers, rooms and reception' },
    { id: 'permissions', label: 'Team access', help: 'Change what each person can do' },
  ] },
];
/**
 * Switches the server doesn't know yet (backend brief, Messages section 28).
 * Until it does, the editor shows them following another switch and nobody
 * can turn them on or off, so a role is never saved with an id the server
 * refuses. They stay out of ALL_CAPS and the built-in roles, which mirror the
 * server. Take an id out of here when the server lists it in `grantable`.
 */
export const PENDING_CAPS = { client_texts: 'clients_all' };

// Every role capability (incl. the derived `catalogue`). Ids MUST match permissionsCatalog.CAPS.
export const ALL_CAPS = CAPABILITY_GROUPS.flatMap((g) => g.caps.map((c) => c.id)).filter((id) => !PENDING_CAPS[id]).concat(['catalogue']);
export const MENU_ROLE_CAPS = ['services', 'products', 'gift_cards'];

/**
 * Reception mode: the extra, front-desk-only parts of screens the Front desk
 * (set up in Settings › Reception) can also have. Ids MUST match
 * permissionsCatalog.DESK_CAPS. Labels live with the switches (deskSwitches.js).
 */
export const DESK_CAPABILITY_GROUPS = [
  { id: 'desk', name: 'Front desk', caps: [
    'pos_page', 'clients_page', 'client_messages', 'gift_card_admin', 'stock', 'forms', 'taxes', 'resources', 'cashup',
    'schedules', 'staff_pay', 'card_readers', 'salon_details', 'booking_settings', 'ai_receptionist', 'diary_sharing',
    'notification_settings', 'data_import', 'finish_setup', 'billing', 'locations', 'account', 'bailey', 'bailey_approvals',
  ].map((id) => ({ id })) },
];
export const FRONT_DESK_KEY = 'front_desk';
/** The groups a role's editor shows. (The Front desk is set up in Settings › Reception.) */
export function capabilityGroupsFor() { return CAPABILITY_GROUPS; }
export const capLabel = (id) => { for (const g of CAPABILITY_GROUPS) { const c = g.caps.find((x) => x.id === id); if (c) return c.label; } return id; };

/**
 * Capabilities only the account holder has for now — they need the server to
 * act for the salon (sender, wallet, payout account) first. Shown, not
 * grantable. The server sends the live list on /roles; this is the fallback.
 */
export const OWNER_ONLY_FALLBACK = ['marketing', 'payouts'];


/** Built-in defaults, for the offline fallback in access.js. Server wins. */
export const BUILT_IN_CAPS = {
  basic: ['client_notes'],
  receptionist: ['diary_all', 'clients_all', 'client_notes', 'pos'],
  standard: ['clients_all', 'client_notes', 'pos', 'discounts'],
  manager: ['diary_all', 'clients_all', 'client_notes', 'clients_export', 'pos', 'discounts', 'discount_codes', 'reports', 'refunds', 'team', 'services', 'products', 'gift_cards', 'catalogue', 'settings'],
  owner: ALL_CAPS.slice(),
};

/** Where a person's role came from, in plain words. */
export function sourceLabel(source) {
  if (source === 'default') return 'Default';
  if (source === 'account-holder') return 'Account holder';
  if (source === 'group-owner') return 'Owner';
  return null;
}

/** GET /roles → { roles, capabilities, ownerOnly, canManage, grantable, scope } */
export async function fetchRoles() {
  const res = await permissionsService.roles();
  return {
    roles: (res && res.roles) || [],
    ownerOnly: (res && res.ownerOnly) || OWNER_ONLY_FALLBACK,
    // In a group only the GROUP owner edits roles (the server refuses anyone
    // else); canEditRoles says so. Older servers don't send it.
    canManage: !!(res && res.canManage) && !(res && res.canEditRoles === false),
    groupLocked: !!(res && res.canManage && res.canEditRoles === false),
    grantable: (res && res.grantable) || [],
    scope: (res && res.scope) || { type: 'salon', sites: 1 },
  };
}

/** GET /people → [{ userId, name, roleKey, source, accessOnly, worksAt, salonIds, isAccountHolder }] */
export async function fetchPeople() {
  const res = await permissionsService.people();
  return (res && res.people) || [];
}

export const createRole = (role) => permissionsService.createRole({ name: role.name, description: role.description, caps: role.caps });
export const saveRole = (role) => permissionsService.updateRole(role.key, { name: role.name, description: role.description, caps: role.caps });
export const resetRole = (key) => permissionsService.resetRole(key);
export const deleteRole = (key) => permissionsService.deleteRole(key);
export const setAssignment = (userId, { roleKey, salonIds }) => permissionsService.setAssignment(userId, salonIds === undefined ? { roleKey } : { roleKey, salonIds });

/** Roles someone can be given: never Owner (it's the account holder), never Front desk (it's for a locked device). */
export function assignableRoles(roles) {
  return (roles || []).filter((r) => !r.owner && !r.frontDesk && r.assignable !== false);
}

/** Error text from a rejected call. */
export function errorText(e, fallback) {
  if (e && e.message === 'Network Error') return 'Couldn\'t reach Beu. Check your connection and try again.';
  return (e && (e.message || (e.response && e.response.data && e.response.data.message))) || fallback || 'Something went wrong. Please try again.';
}
